import React from 'react';
import { Crown, Clock, Sparkles, Star, X, Rocket, AlertTriangle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';

/**
 * OrgPremiumAccessBanner - Shows premium access status for users with org-granted premium
 * Displays in user dashboard when user has active org_premium_access
 * Also shows upgrade prompt when premium has expired
 */
const OrgPremiumAccessBanner = ({ user, onDismiss }) => {
  const navigate = useNavigate();
  
  // Check if user has org premium access
  const orgPremiumAccess = user?.org_premium_access;
  
  if (!orgPremiumAccess?.enabled) {
    return null;
  }
  
  // Calculate days remaining
  const expiresAt = orgPremiumAccess.expires_at ? new Date(orgPremiumAccess.expires_at) : null;
  const now = new Date();
  const daysRemaining = expiresAt 
    ? Math.ceil((expiresAt - now) / (1000 * 60 * 60 * 24))
    : 0;
  
  const orgName = orgPremiumAccess.org_name || 'Your Organization';
  
  // Check if access has expired - show upgrade banner instead
  if (daysRemaining <= 0) {
    return (
      <div className="mb-6 rounded-xl border overflow-hidden bg-gradient-to-r from-gray-800 to-gray-900 border-gray-700">
        <div className="px-4 py-4 sm:px-6 sm:py-5 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          {/* Left side - Icon and message */}
          <div className="flex items-center gap-4">
            <div className="flex-shrink-0 p-3 rounded-xl bg-gradient-to-br from-amber-500 to-orange-500">
              <AlertTriangle className="h-6 w-6 text-white" />
            </div>
            
            <div>
              <div className="flex items-center gap-2 flex-wrap">
                <h3 className="font-semibold text-white text-base sm:text-lg">
                  Your Premium Access Has Expired
                </h3>
              </div>
              <p className="text-sm text-gray-300 mt-1">
                Your premium access through <strong>{orgName}</strong> has ended. Upgrade now to keep using premium tools!
              </p>
            </div>
          </div>
          
          {/* Right side - CTA */}
          <div className="flex items-center gap-3">
            <Button
              onClick={() => navigate('/pricing')}
              className="bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white font-semibold px-6"
            >
              <Rocket className="h-4 w-4 mr-2" />
              Upgrade Now
            </Button>
            
            {onDismiss && (
              <button
                onClick={onDismiss}
                className="p-1 rounded-lg text-gray-400 hover:bg-gray-700 transition-colors"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }
  
  // Determine urgency level for styling
  const isUrgent = daysRemaining <= 7;
  const isModerate = daysRemaining <= 30 && daysRemaining > 7;
  
  // Get access type description
  const accessType = orgPremiumAccess.full_premium_access 
    ? 'Full Platform Access' 
    : orgPremiumAccess.premium_tools?.length 
      ? `${orgPremiumAccess.premium_tools.length} Premium Tool${orgPremiumAccess.premium_tools.length !== 1 ? 's' : ''}`
      : 'Premium Access';
  
  return (
    <div className={`mb-6 rounded-xl border overflow-hidden ${
      isUrgent 
        ? 'bg-gradient-to-r from-red-50 to-orange-50 border-red-200' 
        : isModerate 
          ? 'bg-gradient-to-r from-amber-50 to-yellow-50 border-amber-200'
          : 'bg-gradient-to-r from-emerald-50 to-teal-50 border-emerald-200'
    }`}>
      <div className="px-4 py-3 sm:px-6 sm:py-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
        {/* Left side - Icon and message */}
        <div className="flex items-center gap-3 sm:gap-4">
          <div className={`flex-shrink-0 p-2 sm:p-3 rounded-xl ${
            isUrgent 
              ? 'bg-red-100' 
              : isModerate 
                ? 'bg-amber-100'
                : 'bg-emerald-100'
          }`}>
            {isUrgent ? (
              <Clock className={`h-5 w-5 sm:h-6 sm:w-6 text-red-600`} />
            ) : (
              <Crown className={`h-5 w-5 sm:h-6 sm:w-6 ${
                isModerate ? 'text-amber-600' : 'text-emerald-600'
              }`} />
            )}
          </div>
          
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className={`font-semibold text-sm sm:text-base ${
                isUrgent 
                  ? 'text-red-900' 
                  : isModerate 
                    ? 'text-amber-900'
                    : 'text-emerald-900'
              }`}>
                Premium Access via {orgName}
              </h3>
              <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${
                isUrgent 
                  ? 'bg-red-100 text-red-700' 
                  : isModerate 
                    ? 'bg-amber-100 text-amber-700'
                    : 'bg-emerald-100 text-emerald-700'
              }`}>
                <Sparkles className="h-3 w-3" />
                {accessType}
              </span>
            </div>
            <p className={`text-xs sm:text-sm mt-0.5 ${
              isUrgent 
                ? 'text-red-700' 
                : isModerate 
                  ? 'text-amber-700'
                  : 'text-emerald-700'
            }`}>
              {isUrgent ? (
                <span className="flex items-center gap-1">
                  <Clock className="h-3 w-3" />
                  Only <strong>{daysRemaining}</strong> {daysRemaining === 1 ? 'day' : 'days'} remaining - upgrade to keep access!
                </span>
              ) : (
                <span>
                  <strong>{daysRemaining}</strong> {daysRemaining === 1 ? 'day' : 'days'} of premium access remaining
                </span>
              )}
            </p>
          </div>
        </div>
        
        {/* Right side - Days badge */}
        <div className="flex items-center gap-2 sm:gap-3">
          <div className={`flex items-center gap-2 px-3 py-1.5 rounded-lg ${
            isUrgent 
              ? 'bg-red-100' 
              : isModerate 
                ? 'bg-amber-100'
                : 'bg-emerald-100'
          }`}>
            <Star className={`h-4 w-4 ${
              isUrgent 
                ? 'text-red-600' 
                : isModerate 
                  ? 'text-amber-600'
                  : 'text-emerald-600'
            }`} />
            <span className={`font-bold text-lg ${
              isUrgent 
                ? 'text-red-700' 
                : isModerate 
                  ? 'text-amber-700'
                  : 'text-emerald-700'
            }`}>
              {daysRemaining}
            </span>
            <span className={`text-xs ${
              isUrgent 
                ? 'text-red-600' 
                : isModerate 
                  ? 'text-amber-600'
                  : 'text-emerald-600'
            }`}>
              {daysRemaining === 1 ? 'day' : 'days'}
            </span>
          </div>
          
          {onDismiss && (
            <button
              onClick={onDismiss}
              className={`p-1 rounded-lg transition-colors ${
                isUrgent 
                  ? 'text-red-400 hover:bg-red-100' 
                  : isModerate 
                    ? 'text-amber-400 hover:bg-amber-100'
                    : 'text-emerald-400 hover:bg-emerald-100'
              }`}
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default OrgPremiumAccessBanner;
